/* Kiro · cart → checkout measurement (implementation_version cart_tracking_v2)
   Signals from the GA4 + Shopify implementation brief, built on the real cart data, not on page visits or icon clicks:
   Cart (each one is read from Shopify's /cart.js after the theme's own add/update/change/clear call succeeds):
   - view_cart               every genuine opening of the cart drawer (.cartContent gets "show2"), with the full basket
   - cart_closed             every genuine closing: open_seconds, checkout_clicked, close_method, basket and offer
   - cart_started            once per cart journey, the first time the bag holds a paid product (free gifts don't count)
   - cart_updated            after every settled basket change: paid/gift units, totals, offer and gift status, change_reason,
                             mutation_source (the script(s) that changed the cart). Not sent for an empty bag with no
                             earlier snapshot (first visit, replaced cart): that is a baseline, not a change.
   - two_paid_products_added when the bag moves to exactly two paid units, with what happened to the gift
   - gift_removed            when a previously claimed gift disappears (removal_reason customer / unknown, mutation_source)
   - offer_error             when the offer check (discount-validation) rejects or fails an offer. Once per basket
                             contents + code + error_type + offer_source, remembered across pages (the theme re-runs the
                             same check on every page and cart render). trigger: page_load / cart_render / coupon_click.
   (v2, 28 Oct 2026: offer_error de-duplicated, trigger and mutation_source added, empty-bag baseline not sent.)
   (Renamed 16 Oct 2026 from the brief's kiro_cart_started / kiro_cart_state / kiro_two_paid_state /
    kiro_gift_removed / kiro_offer_error. Use these new names in the GA4 funnel reports.
    Also renamed: kiro_cart_closed, kiro_checkout_click / _open / _error / _closed -> cart_closed, checkout_click,
    checkout_open, checkout_error, checkout_closed.)
   Checkout:
   - checkout_click          an enabled Secure Checkout / Buy Now click (new checkout_attempt_id each time)
   - checkout_open           ONLY from a confirmed GoKwik signal (GOKWIK_OPEN_EVENT). Never inferred from the click.
   - checkout_error          GoKwik error signal (GOKWIK_ERROR_EVENT), or no checkout_initiated (or confirmed open)
                             within OPEN_TIMEOUT_MS. A timeout is not proof of a technical failure.
   - checkout_closed         GoKwik's own "modal_closed" event (confirmed present on the page), with the attempt id
   - checkout_initiated      GoKwik's "checkout_initiated" signal (its checkout app started; not proof it was usable)
   - checkout_step           GoKwik checkout progression: checkout_started, mobile_added, address_step, address_added,
                             payment_step (step name only, no customer data)
   - checkout_redirect       GoKwik sent the shopper to Shopify's own checkout instead
   - checkout_completed      GoKwik's orderSuccess message, with the order reference as transaction_id (purchase
                             itself stays with its current sender)
   Cart A/B test (GoKwik cart vs custom cart, split by the script at the top of theme.liquid):
   - every event above carries cart_variant (gokwik / custom) and assignment_method (url / default); both are also
     set as GA4 user properties. Compare the two carts on assignment_method = url.
   - view_cart also fires when GoKwik's side cart opens (cart_ui: gokwik; the theme's drawer is cart_ui: custom)
   - cart_test_assigned          once per browser, when the visitor is first put in a group (assignment_method)
   - cart_test_initiate_checkout once per checkout opening, for both carts (GoKwik checkout_initiated or its
                                 checkoutStarted step, whichever comes first), with cart_id, value and currency
   - cart_test_purchase          GoKwik's orderSuccess, with cart_id, transaction_id, value and currency
   cart_id is the cart_tracking_id (also saved on the order). The two cart_test_ checkout/purchase events also carry
   cart_token: the Shopify cart token without its ?key= part (the secret), the only place the token is sent.
   Cart attributes _kiro_cart_tracking_id / _kiro_checkout_attempt_id link the Shopify order back to the journey,
   and _kiro_cart_variant records which cart the order came from.
   page_view, add_to_cart, remove_from_cart and purchase stay with their current sender (Shopify Google & YouTube app).
   Sent only to GA4 (send_to), never to the Google Ads accounts. No email, phone, checkout URL or customer-typed
   coupon text is sent, and no cart token except the key-less cart_token on the cart_test_ events. Debug: localStorage.setItem('kiro_ct_debug','1') logs every event. */
(function () {
  'use strict';
  if (window.KiroCheckoutTracking) return;

  var GA4_ID = 'G-3PY229QYY6';                 // GA4 web stream (sent by the Google & YouTube app)
  var VERSION = 'cart_tracking_v2';
  var COUNTRY = 'IN';                          // item ids match the Google & YouTube app: shopify_IN_<product>_<variant>
  // GoKwik signals: set these to the event names GoKwik confirms (gokwikSdk.on(name, fn)). Left null on purpose:
  // the brief says not to guess SDK event names. 'modal_closed' is the only one confirmed so far.
  var GOKWIK_OPEN_EVENT = null;
  var GOKWIK_ERROR_EVENT = null;
  var GOKWIK_CLOSE_EVENT = 'modal_closed';
  // Seen in GoKwik's own gokwik.js: the checkout iframe posts {type:'checkout_initiated'} and the page re-emits it as
  // gokwikSdk.emit('checkout_initiated'). It proves GoKwik's checkout app started, NOT that it was usable, so it is
  // reported as checkout_initiated and does not count as checkout_open until GoKwik confirms its meaning.
  var GOKWIK_INITIATED_EVENT = 'checkout_initiated';
  var GOKWIK_ORIGIN = /(^|\.)gokwik\.co$/;      // messages from GoKwik's checkout iframe
  var CHECKOUT_STEPS = {                       // GoKwik step message -> checkout_step value
    checkoutStarted: 'checkout_started', mobileAddedGK: 'mobile_added', addressStep: 'address_step',
    addressAdded: 'address_added', paymentStep: 'payment_step'
  };
  var OPEN_TIMEOUT_MS = 60000;                 // diagnostic limit from the brief, not a GoKwik SLA

  // Promotional gift variants (same list main-minified.js used for gift auto-removal). A line with one of these ids
  // is a gift, not paid merchandise. Add new gift variants here when an offer changes.
  var GIFT_VARIANT_IDS = [
    '39648806437043', '40576507445427', '40576527433907', '46544319217915', '46761442902267', '47824103571707',
    '49009589616891', '49015339843835', '49015357931771', '49015362420987', '49015363436795', '49015367500027',
    '49056947699963', '49056996884731', '49056997343483', '49056997376251', '49056997441787', '49056997474555',
    '50525149200635'
  ];
  // Offer codes the theme applies itself. Anything else (e.g. text a customer typed) is reported as "customer_code".
  var KNOWN_OFFER_CODES = [
    'BOGOGIFT', 'BULLETBOGO', 'FLAT25', 'FLAT30', 'FREEBAG', 'FREEDC', 'FREEDC25', 'FREEDC30', 'FREEDCHB', 'FREEDCHB25',
    'FREEDCHB30', 'FREEDN', 'FREEDN25', 'FREEDN30', 'FREEDNHB', 'FREEDNHB25', 'FREEDNHB30', 'FREEEYESHADOW',
    'FREEGLOSSMINI', 'FREELIPLINER', 'FREELIPOIL', 'FREEMINI', 'FREENC', 'FREENC25', 'FREENC30', 'FREENCHB',
    'FREENCHB25', 'FREENCHB30', 'FREENEWKAJAL', 'FREEPOUCH', 'FREEPRIMER', 'KIROB1G1TEST', 'KIROB2G1', 'KIROBOGO',
    'KIROMEN', 'KiroFace1', 'KiroFace2'
  ];
  var GIFT_OFFER_CODES = ['FREEEYESHADOW', 'FREEGLOSSMINI', 'FREELIPLINER', 'FREELIPOIL', 'FREEMINI', 'FREENEWKAJAL',
    'FREEPOUCH', 'FREEPRIMER', 'FREEBAG'];

  var STORE_KEY = 'kiro_ct_journey';
  var VARIANT_KEY = 'kiro_cart_variant';        // written by the A/B split script in theme.liquid
  var debug = false;
  try { debug = localStorage.getItem('kiro_ct_debug') === '1'; } catch (e) {}
  var testTraffic = !!(window.Shopify && Shopify.theme && Shopify.theme.role && Shopify.theme.role !== 'main');
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  function rid(prefix) {
    var r = (window.crypto && crypto.getRandomValues) ? Array.prototype.map.call(crypto.getRandomValues(new Uint8Array(8)), function (b) {
      return ('0' + b.toString(16)).slice(-2);
    }).join('') : Math.random().toString(16).slice(2) + Date.now().toString(16);
    return prefix + '_' + r;
  }
  function load() { try { return JSON.parse(localStorage.getItem(STORE_KEY)) || null; } catch (e) { return null; } }
  function save() { try { localStorage.setItem(STORE_KEY, JSON.stringify(journey)); } catch (e) {} }
  function now() { return Date.now(); }
  function cartVariant() {
    try {
      var v = localStorage.getItem(VARIANT_KEY);
      return v === 'gokwik' || v === 'custom' ? v : undefined;
    } catch (e) { return undefined; }
  }
  // url (?gokwik_cart= campaign link chose the cart) or default (organic / other UTMs: always the custom cart)
  function assignmentMethod() {
    if (!cartVariant()) return undefined;
    try { return localStorage.getItem(VARIANT_KEY + '_method') || undefined; } catch (e) { return undefined; }
  }


  function send(name, params) {
    var p = Object.assign({ send_to: GA4_ID, implementation_version: VERSION, test_traffic: testTraffic, cart_variant: cartVariant(), assignment_method: assignmentMethod() }, params || {});
    if (debug && window.console) console.log('[Kiro cart tracking]', name, p);
    try { if (typeof window.gtag === 'function') window.gtag('event', name, p); } catch (e) {}
  }

  // ---------- offer (code the theme applied) ----------
  function cookieCode() {
    var m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    return m && m[1] ? decodeURIComponent(m[1]) : '';
  }
  function safeCode(code) {
    if (!code) return 'none';
    return KNOWN_OFFER_CODES.indexOf(code) !== -1 ? code : 'customer_code';
  }
  function offerType(code) {
    if (!code || code === 'none') return 'none';
    if (GIFT_OFFER_CODES.indexOf(code) !== -1) return 'gift';
    if (code === 'KIROB2G1' || code === 'KIROB1G1TEST') return 'b2g1';
    if (/BOGO/i.test(code)) return 'bogo';
    return 'other';
  }
  function currentOffer() {
    // The coupon box shows what is applied once the offer check has finished
    var applied = /applied/i.test((document.querySelector('.coupon-apply-btn') || {}).textContent || '');
    var code = applied ? ((document.querySelector('.coupon-input') || {}).value || '').trim() : '';
    code = safeCode(code || cookieCode());
    return { offer_code: code, offer_type: offerType(code) };
  }
  function offerSettled() {
    // the theme shows its loading dots while the offer price is recalculated
    var btn = (document.querySelector('.coupon-apply-btn') || {}).textContent || '';
    return !document.querySelector('.loader_on_cart') && !/calculating/i.test(btn);
  }
  function whenOfferSettled(fn) {
    var waited = 0;
    (function check() {
      if (offerSettled() || waited >= 6000) return fn();
      waited += 250; setTimeout(check, 250);
    })();
  }

  // ---------- basket ----------
  function isGift(line) { return GIFT_VARIANT_IDS.indexOf(String(line.variant_id)) !== -1; }
  function summarise(cart) {
    var paid = 0, gifts = 0;
    (cart.items || []).forEach(function (it) { if (isGift(it)) gifts += it.quantity; else paid += it.quantity; });
    return {
      paid_unit_count: paid,
      gift_unit_count: gifts,
      paid_qty_band: paid >= 3 ? '3_plus' : String(paid),
      value: Math.round(cart.total_price) / 100,
      currency: cart.currency || 'INR'
    };
  }
  function gaItems(cart) {
    return (cart.items || []).map(function (it, i) {
      return {
        item_id: 'shopify_' + COUNTRY + '_' + it.product_id + '_' + it.variant_id,
        item_name: it.product_title,
        item_variant: it.variant_title || undefined,
        item_category: it.product_type || undefined,
        price: Math.round(it.final_price) / 100,
        quantity: it.quantity,
        index: i,
        item_role: isGift(it) ? 'gift' : 'paid'
      };
    });
  }
  function lineMap(cart) {
    var m = {};
    (cart.items || []).forEach(function (it) { m[it.variant_id] = (m[it.variant_id] || 0) + it.quantity; });
    return m;
  }

  // ---------- customer actions (to tell a customer removal from an unexplained one) ----------
  var lastRemoval = null;    // { variant, at }
  var lastCouponClick = 0;
  var pageMutated = false, drawerEverOpened = false;    // on this page: a basket change was sent / the cart was opened
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (!t) return;
    var rm = t.closest('.remove-item, .qty-decrease, .delete-cart');
    if (rm) {
      var line = rm.closest('.cart-item, [data-variant-id]');
      lastRemoval = { variant: line ? String(line.getAttribute('data-variant-id') || '') : String(rm.getAttribute('var_id') || ''), at: now() };
    }
    if (t.closest('.coupon-apply-btn, .apply-discount-code-btn')) lastCouponClick = now();
  }, true);

  // ---------- cart journey ----------
  var journey = load();          // { id, revision, started, cartKey, attempt, giftEverClaimed, prev, offerErrors }
  var lastCart = null;

  function newJourney(cartKey) {
    return { id: rid('ct'), revision: 0, started: false, cartKey: cartKey || '', attempt: null, giftEverClaimed: false, prev: null, offerErrors: [] };
  }

  // cartKey: the cart token, kept only in this browser to notice a replaced cart. It is never sent to GA4.
  // sources: the scripts whose cart requests led to this read (see callerScript)
  function onCart(cart, isPageLoad, sources) {
    if (!cart || !cart.items) return;
    lastCart = cart;
    var s = summarise(cart);
    var cartKey = cart.token ? String(cart.token).split('?')[0] : '';
    var lines = lineMap(cart);

    // A journey ends when the bag is emptied or the cart is replaced (e.g. after an order).
    if (!journey || journey.ended || (journey.cartKey && cartKey && journey.cartKey !== cartKey)) journey = newJourney(cartKey);
    journey.cartKey = cartKey || journey.cartKey;

    var prev = journey.prev;   // last snapshot of this journey
    var base = { cart_tracking_id: journey.id };

    if (isPageLoad) {
      // Baseline only: an existing basket is reported as returning_cart, never as a new add or a state change
      if (!journey.started && s.paid_unit_count > 0) {
        journey.started = true;
        send('cart_started', Object.assign({}, base, { cart_revision: journey.revision, entry_type: 'returning_cart' }, s));
      }
      if (s.gift_unit_count > 0) journey.giftEverClaimed = true;
      journey.prev = { paid: s.paid_unit_count, gifts: s.gift_unit_count, lines: lines };
      save();
      syncCartAttributes();
      return;
    }

    // No earlier snapshot and an empty bag (first visit, or the cart was replaced): baseline only, nothing changed
    if (!prev && !cart.item_count) {
      journey.prev = { paid: 0, gifts: 0, lines: lines };
      save();
      return;
    }

    // Skip if nothing in the basket actually changed (e.g. the same quantity set again)
    var changed = !prev || JSON.stringify(prev.lines) !== JSON.stringify(lines);
    if (!changed) return;
    journey.revision += 1;
    var rev = journey.revision;
    var mutationSource = (sources && sources.length ? sources.join(',') : 'unknown').slice(0, 100);
    var pPaid = prev ? prev.paid : 0, pGifts = prev ? prev.gifts : 0;

    if (!journey.started && s.paid_unit_count > 0) {
      journey.started = true;
      send('cart_started', Object.assign({}, base, { cart_revision: rev, entry_type: 'new_cart' }, s));
    }

    // Gift removal: a gift present before is gone (or fewer) now
    var giftRemoved = s.gift_unit_count < pGifts;
    var removalReason = 'unknown';
    if (giftRemoved && lastRemoval && now() - lastRemoval.at < 8000 && GIFT_VARIANT_IDS.indexOf(lastRemoval.variant) !== -1) {
      removalReason = 'customer';
    }
    if (s.gift_unit_count > 0) journey.giftEverClaimed = true;

    var change_reason =
      cart.item_count === 0 ? 'cleared' :
      (s.paid_unit_count !== pPaid && s.gift_unit_count !== pGifts) ? 'paid_and_gift_changed' :
      s.paid_unit_count > pPaid ? 'paid_added' :
      s.paid_unit_count < pPaid ? 'paid_removed' :
      s.gift_unit_count > pGifts ? 'gift_claimed' :
      s.gift_unit_count < pGifts ? 'gift_removed' : 'line_changed';

    var giftState =
      s.gift_unit_count > 0 ? (pGifts > 0 ? 'retained' : 'claimed') :
      giftRemoved ? (removalReason === 'customer' ? 'customer_removed' : 'unknown') :
      journey.giftEverClaimed ? 'unknown' : 'none';

    journey.prev = { paid: s.paid_unit_count, gifts: s.gift_unit_count, lines: lines };
    if (cart.item_count === 0) journey.ended = true;     // emptied bag: the next add starts a new journey
    save();
    syncCartAttributes();

    // Offer fields are read after the theme's offer check settles, so they match what the shopper sees
    whenOfferSettled(function () {
      var offer = currentOffer();
      var state = Object.assign({}, base, {
        cart_revision: rev, change_reason: change_reason, gift_state: giftState, mutation_source: mutationSource
      }, s, offer);
      send('cart_updated', state);

      if (giftRemoved) {
        send('gift_removed', Object.assign({}, base, {
          cart_revision: rev,
          removal_reason: removalReason,        // "automatic" only if the cart code reports it; it doesn't today
          mutation_source: mutationSource,      // which script removed it, when the customer didn't
          gift_units_before: pGifts,
          gift_unit_count: s.gift_unit_count,
          paid_unit_count: s.paid_unit_count
        }, offer));
      }
      if (s.paid_unit_count === 2 && pPaid !== 2) {
        send('two_paid_products_added', Object.assign({}, base, {
          cart_revision: rev,
          gift_state: giftState,                 // retained / claimed / customer_removed / unknown / none
          gift_previously_claimed: !!(journey.giftEverClaimed || pGifts > 0)
        }, s, offer));
      }
    });
  }

  function fetchCart() {
    return fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' }).then(function (r) { return r.json(); });
  }
  var refreshTimer = null, pendingSources = [];
  function refreshCart(isPageLoad, source) {
    clearTimeout(refreshTimer);
    if (source && pendingSources.indexOf(source) === -1) pendingSources.push(source);
    // short debounce: several cart calls in a row report one settled basket (with every script that made them)
    refreshTimer = setTimeout(function () {
      var sources = pendingSources;
      pendingSources = [];
      fetchCart().then(function (c) { onCart(c, !!isPageLoad, sources); }).catch(function () {});
    }, isPageLoad ? 0 : 400);
  }

  // Which script started a request. Other scripts wrap XHR/fetch too (GoKwik does, loaded after this file), so the
  // nearest caller is often a wrapper: the outermost frame from the shop's own files is used, else the outermost
  // frame. File name for the shop's assets and Shopify-hosted app files, host name for another site's script,
  // inline_script for code in the page itself, unknown when the browser hides the stack. jQuery is skipped.
  var STACK_URL = /https?:\/\/[^\s()@]+?(?=:\d+:\d+)/g;
  var jqueryFiles = null;
  function jqueryUrls() {
    // the script files that define jQuery, whatever they are called (the theme bundles it as footer_render_js_2.js)
    if (jqueryFiles) return jqueryFiles;
    var found = [];
    [window.jQuery, window.$].forEach(function (jq) {
      if (!jq || typeof jq.each !== 'function') return;
      try {
        jq.each([0], function () {
          var m = (new Error().stack || '').match(STACK_URL) || [];
          if (m[1]) found.push(m[1].split('?')[0]);    // m[0] is this callback, m[1] is jQuery's each
        });
      } catch (e) {}
    });
    if (found.length) jqueryFiles = found;             // not cached until jQuery has loaded
    return found;
  }
  function callerScript() {
    var stack = '', limit = Error.stackTraceLimit;
    try { Error.stackTraceLimit = 50; stack = new Error().stack || ''; } catch (e) {}
    try { Error.stackTraceLimit = limit; } catch (e) {}
    var urls = stack.match(STACK_URL) || [], jq = jqueryUrls();
    var outer = '', outerOwn = '';
    for (var i = 0; i < urls.length; i++) {
      var u;
      try { u = new URL(urls[i]); } catch (e) { continue; }
      var file = u.pathname.split('/').pop() || '';
      if (/kiro-checkout-tracking/.test(file) || /jquery/i.test(file) || jq.indexOf(urls[i].split('?')[0]) !== -1) continue;
      var label = !/\.js$/i.test(file) ? 'inline_script' :
        u.hostname === location.hostname || /(^|\.)shopify\.com$/.test(u.hostname) ? file : u.hostname;
      outer = label;                                    // frames run innermost first: the last one wins
      if (u.hostname === location.hostname) outerOwn = label;
    }
    return (outerOwn || outer || 'unknown').slice(0, 100);
  }

  // ---------- offer check (discount-validation) ----------
  // Why the theme ran the check. It doesn't say, so it is read from this page: a coupon box / offer click just
  // before (coupon_click), else no basket change and no cart opening yet (page_load), else cart_render.
  function offerTrigger() {
    if (now() - lastCouponClick < 6000) return 'coupon_click';
    return pageMutated || drawerEverOpened ? 'cart_render' : 'page_load';
  }
  // The basket the check was run on (the theme sends its cart with the request), as a short hash of variant:qty
  function cartFingerprint(cart) {
    if (!cart || !cart.items) return 'r' + (journey ? journey.revision : 0);
    var m = lineMap(cart);
    var str = Object.keys(m).sort().map(function (k) { return k + ':' + m[k]; }).join(','), h = 0;
    for (var i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
  }
  function onOfferResponse(check, httpOk, body) {
    var status = null;
    if (body) { try { status = JSON.parse(body).statusCode; } catch (e) {} }
    if (httpOk && status === 200) return;
    var error_type = !httpOk ? 'request_failed' : status === 302 ? 'not_eligible' : status === 301 ? 'cart_empty' : 'rejected';
    var safe = safeCode(check.code);
    var source = check.trigger === 'coupon_click' ? 'coupon_box' : 'automatic';

    // Once per basket + code + error type + source: the theme repeats the same check on every page and cart render
    if (!journey) journey = newJourney('');
    journey.offerErrors = journey.offerErrors || [];     // journeys saved by v1 have no list
    var key = [cartFingerprint(check.cart), safe, error_type, source].join('|');
    if (journey.offerErrors.indexOf(key) !== -1) return;
    journey.offerErrors.push(key);
    if (journey.offerErrors.length > 30) journey.offerErrors.shift();
    save();

    send('offer_error', {
      cart_tracking_id: journey.id,
      cart_revision: journey.revision,
      error_type: error_type,
      offer_code: safe,
      offer_type: offerType(safe),
      offer_source: source,
      trigger: check.trigger
    });
  }

  // ---------- watch the theme's own requests (jQuery ajax = XHR, plus fetch) ----------
  var CART_MUTATION = /\/cart\/(add|update|change|clear)(\.js)?(\?|$)/;
  var OFFER_CHECK = /discount-validation/;
  var oOpen = XMLHttpRequest.prototype.open, oSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    try { this.__kiroUrl = String(url); } catch (e) {}
    return oOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    var xhr = this, url = xhr.__kiroUrl || '';
    try {
      if (CART_MUTATION.test(url)) {
        var src = callerScript();
        pageMutated = true;
        xhr.addEventListener('load', function () { if (xhr.status < 400) refreshCart(false, src); });
      } else if (OFFER_CHECK.test(url)) {
        var req = {};
        try { req = JSON.parse(body) || {}; } catch (e) {}
        // read at request time: the trigger depends on what happened just before the check
        var check = { code: req.discount_code || '', cart: req.cart_data, trigger: offerTrigger() };
        xhr.addEventListener('load', function () { onOfferResponse(check, xhr.status < 400, xhr.responseText); });
        xhr.addEventListener('error', function () { onOfferResponse(check, false, null); });
      }
    } catch (e) {}
    return oSend.apply(this, arguments);
  };
  if (window.fetch) {
    var oFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = oFetch.apply(this, arguments);
      try {
        if (CART_MUTATION.test(url)) {
          var src = callerScript();
          pageMutated = true;
          p.then(function (r) { if (r && r.ok) refreshCart(false, src); }, function () {});
        }
      } catch (e) {}
      return p;
    };
  }

  // ---------- view_cart / kiro_cart_closed: every genuine drawer opening and closing ----------
  var drawerOpenedAt = 0, drawerCheckoutClicked = false, lastDrawerClick = null;
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (!t) return;
    var method =
      t.closest('.gokwik-checkout button, #gokwik-buy-now') ? 'checkout' :
      t.closest('.close-cart, .cartContent .fil-heading i') ? 'back_button' :
      t.closest('.body-fade, .cart-overlay') ? 'overlay' :
      t.closest('.cartContent a[href]') ? 'link' : null;     // e.g. Shop All in the bag
    if (method) lastDrawerClick = { method: method, at: now() };
  }, true);
  function watchDrawer() {
    var drawer = document.querySelector('.cartContent');
    if (!drawer) return false;
    var open = drawer.classList.contains('show2');
    new MutationObserver(function () {
      var isOpen = drawer.classList.contains('show2');
      if (!isOpen && open) {
        // genuine close: the drawer loses "show2" (back arrow, overlay, checkout hand-off, ...)
        var recent = lastDrawerClick && now() - lastDrawerClick.at < 1500 ? lastDrawerClick.method : 'other';
        send('cart_closed', Object.assign({
          cart_tracking_id: journey ? journey.id : undefined,
          cart_revision: journey ? journey.revision : undefined,
          open_seconds: drawerOpenedAt ? Math.round((now() - drawerOpenedAt) / 1000) : undefined,
          checkout_clicked: drawerCheckoutClicked,
          close_method: drawerCheckoutClicked && recent === 'other' ? 'checkout' : recent
        }, lastCart ? summarise(lastCart) : {}, currentOffer()));
        drawerOpenedAt = 0;
      }
      if (isOpen && !open) {
        drawerOpenedAt = now();
        drawerEverOpened = true;
        drawerCheckoutClicked = false;
        sendViewCart('custom');
      }
      open = isOpen;     // re-rendering an open drawer keeps "show2", so it doesn't count again
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    return true;
  }
  if (!watchDrawer()) document.addEventListener('DOMContentLoaded', watchDrawer);

  // cart_ui: which drawer opened (custom = the theme's .cartContent, gokwik = GoKwik's side cart)
  function sendViewCart(cartUi) {
    // read the basket at the moment it opens (opening right after an add shows the new basket)
    setTimeout(function () {
      fetchCart().then(function (c) {
        lastCart = c;
        send('view_cart', Object.assign({
          cart_tracking_id: journey ? journey.id : undefined,
          cart_revision: journey ? journey.revision : undefined,
          cart_ui: cartUi,
          items: gaItems(c)
        }, summarise(c)));
      }).catch(function () {});
    }, 500);
  }

  // GoKwik's side cart: theme.liquid announces every open (cart icon, auto-open after add to cart, /cart redirect).
  // Several triggers can fire for one opening, so opens within 1.5s count once.
  var lastGokwikOpenAt = 0;
  document.addEventListener('kiro:gokwik-cart-open', function () {
    if (now() - lastGokwikOpenAt < 1500) return;
    lastGokwikOpenAt = now();
    sendViewCart('gokwik');
  });

  // ---------- checkout attempts ----------
  var openTimer = null;
  function clickSource(el) { return el.closest('#gokwik-buy-now') ? 'buy_now' : 'cart_drawer'; }
  function onCheckoutClick(btn) {
    if (!btn || btn.disabled || btn.classList.contains('disabled')) return;
    if (!journey) journey = newJourney('');
    var attempt = { id: rid('ca'), opened: false, initiated: false, ended: false, at: now() };
    journey.attempt = attempt;
    if (drawerOpenedAt) drawerCheckoutClicked = true;
    save();
    send('checkout_click', Object.assign({
      cart_tracking_id: journey.id,
      cart_revision: journey.revision,
      checkout_attempt_id: attempt.id,
      checkout_source: clickSource(btn)
    }, lastCart ? summarise(lastCart) : {}));
    // best effort: the attempt id may reach the order if GoKwik reads the cart after this lands
    syncCartAttributes(attempt.id);

    clearTimeout(openTimer);
    // Wait for the confirmed open signal if GoKwik gives us one, otherwise for its checkout_initiated signal.
    // No signal in time is reported as a timeout, which is not proof of a technical failure.
    if (GOKWIK_OPEN_EVENT || GOKWIK_INITIATED_EVENT) {
      openTimer = setTimeout(function () {
        if (attempt.ended || (GOKWIK_OPEN_EVENT ? attempt.opened : attempt.initiated)) return;
        attempt.ended = true;
        save();
        send('checkout_error', {
          cart_tracking_id: journey.id, checkout_attempt_id: attempt.id,
          error_type: GOKWIK_OPEN_EVENT ? 'open_timeout' : 'initiated_timeout'
        });
      }, OPEN_TIMEOUT_MS);
    }
  }

  // Our ids on the Shopify cart (hidden "_" attributes). They are copied to the order, so a completed order can be
  // joined back to the cart journey and checkout attempt. Opaque random ids only, no customer data.
  function syncCartAttributes(attemptId) {
    if (!journey || !lastCart || !lastCart.item_count) return;
    var cur = lastCart.attributes || {};
    var attrs = {};
    var variant = cartVariant();
    if (cur._kiro_cart_tracking_id !== journey.id) attrs._kiro_cart_tracking_id = journey.id;
    if (variant && cur._kiro_cart_variant !== variant) attrs._kiro_cart_variant = variant;
    if (attemptId && cur._kiro_checkout_attempt_id !== attemptId) attrs._kiro_checkout_attempt_id = attemptId;
    if (!Object.keys(attrs).length) return;
    Object.keys(attrs).forEach(function (k) { cur[k] = attrs[k]; });
    lastCart.attributes = cur;
    try {
      // the original fetch: our own attribute write is not a basket change, so it skips the cart watch above
      (oFetch || window.fetch).call(window, root + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin', keepalive: true,
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: attrs })
      }).catch(function () {});
    } catch (e) {}
  }
  // capture phase: runs before GoKwik's own onclick handler takes over the page
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.gokwik-checkout button, #gokwik-buy-now');
    if (btn) onCheckoutClick(btn);
  }, true);

  function currentAttempt() { return journey && journey.attempt; }
  function checkoutOpened() {
    var a = currentAttempt();
    if (!a || a.opened || a.ended) return;          // once per attempt
    a.opened = true;
    clearTimeout(openTimer);
    save();
    send('checkout_open', { cart_tracking_id: journey.id, cart_revision: journey.revision, checkout_attempt_id: a.id });
  }
  function checkoutError() {
    var a = currentAttempt();
    if (!a || a.opened || a.ended) return;
    a.ended = true;
    clearTimeout(openTimer);
    save();
    send('checkout_error', { cart_tracking_id: journey.id, checkout_attempt_id: a.id, error_type: 'provider_error' });
  }
  // ---------- cart A/B test ----------
  // GoKwik-cart shoppers start checkout inside GoKwik's drawer, so there is no checkout_click or attempt for them.
  // These events don't depend on an attempt: they come straight from GoKwik's checkout signals for both carts.
  var testCheckoutOpen = false;     // one cart_test_initiate_checkout per checkout opening
  function cartTestParams(extra) {
    return Object.assign({
      cart_id: journey ? journey.id : undefined,
      cart_token: journey && journey.cartKey ? journey.cartKey : undefined    // token without its ?key= secret
    }, lastCart ? {
      value: Math.round(lastCart.total_price) / 100,
      currency: lastCart.currency || 'INR'
    } : {}, extra || {});
  }
  function cartTestInitiateCheckout() {
    if (testCheckoutOpen || !cartVariant()) return;
    testCheckoutOpen = true;
    send('cart_test_initiate_checkout', cartTestParams());
  }
  // returns false only while gtag isn't ready yet, so the caller can retry
  function cartTestAssigned() {
    var variant = cartVariant();
    if (!variant) return true;
    if (typeof window.gtag !== 'function') return false;
    try { window.gtag('set', 'user_properties', { cart_variant: variant, assignment_method: assignmentMethod() }); } catch (e) {}
    // once per browser (again only if the group changes, e.g. through the URL override)
    var reported = null;
    try { reported = localStorage.getItem(VARIANT_KEY + '_reported'); } catch (e) {}
    if (reported === variant) return true;
    send('cart_test_assigned');     // send() adds cart_variant and assignment_method
    try { localStorage.setItem(VARIANT_KEY + '_reported', variant); } catch (e) {}
    return true;
  }

  function checkoutClosed() {
    testCheckoutOpen = false;
    var a = currentAttempt();
    if (!a) return;
    send('checkout_closed', {
      cart_tracking_id: journey.id, checkout_attempt_id: a.id,
      checkout_opened: a.opened, checkout_initiated: !!a.initiated, order_completed: !!a.completed
    });
  }
  function checkoutInitiated() {
    cartTestInitiateCheckout();
    var a = currentAttempt();
    if (!a || a.initiated) return;                   // once per attempt
    a.initiated = true;
    if (!GOKWIK_OPEN_EVENT) clearTimeout(openTimer);
    save();
    send('checkout_initiated', {
      cart_tracking_id: journey.id, cart_revision: journey.revision, checkout_attempt_id: a.id,
      seconds_since_click: a.at ? Math.round((now() - a.at) / 1000) : undefined
    });
  }

  // Messages GoKwik's checkout iframe sends to this page (seen in gokwik.js). Only the order reference is read;
  // the rest of the message (which can include customer details) is ignored.
  function orderReference(resp) {
    var keys = ['orderName', 'order_name', 'moid', 'shopifyOrderName', 'shopify_order_name'];
    var srcs = [resp, resp && resp.data];
    for (var i = 0; i < srcs.length; i++) {
      var o = srcs[i];
      if (!o || typeof o !== 'object') continue;
      for (var j = 0; j < keys.length; j++) {
        var v = o[keys[j]];
        if ((typeof v === 'string' || typeof v === 'number') && String(v).length <= 30 && String(v).indexOf('@') === -1) return String(v);
      }
    }
    return undefined;
  }
  window.addEventListener('message', function (e) {
    var host = '';
    try { host = new URL(e.origin).hostname; } catch (_) {}
    if (!GOKWIK_ORIGIN.test(host) || !e.data || typeof e.data !== 'object') return;
    var a = currentAttempt();
    // Checkout progression, from the step messages GoKwik sends for Shopify analytics (seen on the dev preview:
    // checkoutStarted, mobileAddedGK, addressStep, addressAdded, paymentStep). Only the step name is read.
    // Steps can be auto-completed for returning KwikPass shoppers, so "added" means reached, not typed.
    if (e.data.analyticsName === 'shopifyAnalytics' && CHECKOUT_STEPS[e.data.eventName]) {
      var step = CHECKOUT_STEPS[e.data.eventName];
      if (step === 'checkout_started') cartTestInitiateCheckout();   // backup if checkout_initiated didn't fire
      if (a) {
        a.steps = a.steps || {};
        if (a.steps[step]) return;                    // once per attempt per step
        a.steps[step] = true;
        save();
      }
      send('checkout_step', {
        cart_tracking_id: journey ? journey.id : undefined,
        checkout_attempt_id: a ? a.id : undefined,
        checkout_step: step,
        seconds_since_click: a && a.at ? Math.round((now() - a.at) / 1000) : undefined
      });
      return;
    }
    if (e.data.type === 'orderSuccess') {
      if (a) { a.completed = true; a.ended = true; clearTimeout(openTimer); save(); }
      // A confirmed GoKwik order. Named checkout_completed, not "purchase": purchase stays with its current sender.
      send('checkout_completed', {
        cart_tracking_id: journey ? journey.id : undefined,
        checkout_attempt_id: a ? a.id : undefined,
        transaction_id: orderReference(e.data.response)
      });
      if (cartVariant()) send('cart_test_purchase', cartTestParams({ transaction_id: orderReference(e.data.response) }));
      testCheckoutOpen = false;
    } else if (e.data.type === 'shopifyRedirect') {
      // GoKwik handed the shopper to Shopify's own checkout instead of opening its checkout
      testCheckoutOpen = false;
      if (a && !a.ended) { a.ended = true; clearTimeout(openTimer); save(); }
      send('checkout_redirect', {
        cart_tracking_id: journey ? journey.id : undefined,
        checkout_attempt_id: a ? a.id : undefined,
        redirect_target: 'shopify_checkout'
      });
    }
  });

  // GoKwik's SDK loads after this file; subscribe once it exists.
  var subscribed = false;
  function subscribeGokwik() {
    var sdk = window.gokwikSdk;
    if (subscribed || !sdk || typeof sdk.on !== 'function') return !!subscribed;
    subscribed = true;
    if (GOKWIK_OPEN_EVENT) sdk.on(GOKWIK_OPEN_EVENT, checkoutOpened);
    if (GOKWIK_INITIATED_EVENT) sdk.on(GOKWIK_INITIATED_EVENT, checkoutInitiated);
    if (GOKWIK_ERROR_EVENT) sdk.on(GOKWIK_ERROR_EVENT, checkoutError);
    if (GOKWIK_CLOSE_EVENT) sdk.on(GOKWIK_CLOSE_EVENT, checkoutClosed);
    return true;
  }
  if (!subscribeGokwik()) {
    var tries = 0;
    var iv = setInterval(function () { if (subscribeGokwik() || ++tries > 60) clearInterval(iv); }, 500);
  }

  // GA's gtag can load after this file; keep trying for up to 30s so the assignment isn't skipped on every page.
  if (!cartTestAssigned()) {
    var assignTries = 0;
    var assignIv = setInterval(function () { if (cartTestAssigned() || ++assignTries > 60) clearInterval(assignIv); }, 500);
  }

  // Baseline on page load
  refreshCart(true);

  // Small API for other scripts (or a GoKwik callback once confirmed): KiroCheckoutTracking.checkoutOpened() etc.
  window.KiroCheckoutTracking = {
    version: VERSION,
    checkoutOpened: checkoutOpened,
    checkoutError: checkoutError,
    checkoutClosed: checkoutClosed,
    journey: function () { return journey; }
  };
})();
